import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';

import { PaymentOrderType } from '../../utils/payment-flow.util';

interface PaymentIframeModalData {
  paymentUrl: string;
  orderId?: string;
  orderType?: PaymentOrderType;
}

interface PaymentFinishedMessage {
  source?: string;
  type?: string;
  payload?: {
    orderType?: PaymentOrderType;
    orderId?: string;
    status?: string;
  };
}

@Component({
  selector: 'payment-iframe-modal',
  standalone: true,
  imports: [CommonModule, I18nPipe, NzButtonModule, NzIconModule, TestIdDirective],
  templateUrl: './payment-iframe-modal.component.html',
  styleUrls: ['./payment-iframe-modal.component.less']
})
export class PaymentIframeModalComponent implements OnInit, OnDestroy {
  private readonly modalRef = inject(NzModalRef);
  private readonly sanitizer = inject(DomSanitizer);
  readonly modalData: PaymentIframeModalData = inject(NZ_MODAL_DATA, { optional: true }) || { paymentUrl: '' };

  safePaymentUrl: SafeResourceUrl | null = null;
  private readonly onMessage = (event: MessageEvent<PaymentFinishedMessage>) => this.handleMessage(event);

  ngOnInit(): void {
    if (this.modalData.paymentUrl) {
      this.safePaymentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.modalData.paymentUrl);
    }
    window.addEventListener('message', this.onMessage);
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.onMessage);
  }

  openInNewWindow(): void {
    if (!this.modalData.paymentUrl) return;
    window.open(this.modalData.paymentUrl, 'aile-payment', this.getPaymentPopupFeatures());
  }

  close(): void {
    this.modalRef.close();
  }

  private handleMessage(event: MessageEvent<PaymentFinishedMessage>): void {
    if (event.origin !== window.location.origin) return;
    const data = event.data;
    if (data?.source !== 'aile-admin' || data?.type !== 'payment.finished') return;
    if (this.modalData.orderType && data.payload?.orderType !== this.modalData.orderType) return;
    if (this.modalData.orderId && data.payload?.orderId !== this.modalData.orderId) return;
    this.modalRef.close({
      refresh: true,
      paymentStatus: data.payload?.status,
      orderId: data.payload?.orderId,
      orderType: data.payload?.orderType
    });
  }

  private getPaymentPopupFeatures(): string {
    const width = 1200;
    const height = 800;
    const left = Math.max(0, Math.round((window.screen.width - width) / 2));
    const top = Math.max(0, Math.round((window.screen.height - height) / 2));
    return `popup=yes,width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`;
  }
}
